import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

export type SystemEventSeverity = 'info' | 'warning' | 'error' | 'critical';

export type SystemEventModule =
  | 'import'
  | 'auth'
  | 'billing'
  | 'cascade'
  | 'route'
  | 'save'
  | 'load'
  | 'permission'
  | 'onboarding'
  | 'notification'
  | 'unknown';

interface LogOptions {
  severity?: SystemEventSeverity;
  module?: SystemEventModule;
  message: string;
  context?: Record<string, unknown>;
  stack?: string | null;
  route?: string | null;
  agencyId?: string | null;
  brandId?: string | null;
}

/**
 * Write an event to the system_events audit log. Best-effort: never throws,
 * never blocks the caller. Visible only to super admins via /superadmin/readiness.
 */
export async function logSystemEvent(opts: LogOptions): Promise<void> {
  try {
    const { data: auth } = await supabase.auth.getUser();
    const route = opts.route ?? (typeof window !== 'undefined' ? window.location.pathname : null);
    await (supabase as any).from('system_events').insert({
      severity: opts.severity ?? 'error',
      module: opts.module ?? 'unknown',
      message: opts.message.slice(0, 2000),
      context: opts.context ?? {},
      stack: opts.stack ? opts.stack.slice(0, 8000) : null,
      route,
      user_id: auth?.user?.id ?? null,
      agency_id: opts.agencyId ?? null,
      brand_id: opts.brandId ?? null,
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[logSystemEvent] failed', e);
  }
}

function extractErr(err: unknown): { message: string; stack?: string; code?: string; details?: string } {
  if (err && typeof err === 'object') {
    const e = err as any;
    // Postgres / PostgREST shape
    if (e.code || e.details || e.hint) {
      const msg = e.message || e.details || e.hint || 'Database error';
      return { message: msg, stack: e.stack, code: e.code, details: e.details };
    }
    if (err instanceof Error) return { message: err.message, stack: err.stack };
    try { return { message: JSON.stringify(err) }; } catch { /* fall-through */ }
  }
  if (typeof err === 'string') return { message: err };
  return { message: 'Unknown error' };
}

/** Produce a short, user-readable detail string from a Supabase/PG error. */
function readableDetail(err: unknown): string | undefined {
  const { message, code } = extractErr(err);
  if (!message) return undefined;
  // Strip noisy Postgres wrappers, keep it tight.
  let m = message.replace(/^new row for relation .*? violates check constraint "(.+?)"/, 'Invalid value for $1');
  m = m.replace(/duplicate key value violates unique constraint "(.+?)"/, 'Already exists ($1)');
  m = m.replace(/null value in column "(.+?)" .*/, 'Required field missing: $1');
  m = m.replace(/permission denied for .*/, 'Permission denied');
  if (m.length > 220) m = m.slice(0, 220) + '…';
  return code ? `${m} [${code}]` : m;
}

/**
 * Production-safe error handler.
 * - Logs full error details to the console for developers.
 * - Shows a friendly headline + the actual backend detail to the user.
 * - Records a system_events row for super-admin observability.
 */
export function reportError(
  scope: string,
  err: unknown,
  userMessage?: string,
  opts?: { module?: SystemEventModule; severity?: SystemEventSeverity; context?: Record<string, unknown>; agencyId?: string | null; brandId?: string | null }
) {
  // eslint-disable-next-line no-console
  console.error(`[${scope}]`, err);
  const detail = readableDetail(err);
  toast.error(userMessage ?? "Something went wrong. Please try again.", detail ? { description: detail } : undefined);
  const { message, stack, code } = extractErr(err);
  void logSystemEvent({
    severity: opts?.severity ?? 'error',
    module: opts?.module ?? 'unknown',
    message: `[${scope}] ${message}`,
    stack: stack ?? null,
    context: { ...(opts?.context ?? {}), code },
    agencyId: opts?.agencyId ?? null,
    brandId: opts?.brandId ?? null,
  });
}

export function reportSaveError(scope: string, err: unknown, opts?: Parameters<typeof reportError>[3]) {
  reportError(scope, err, "We couldn't save your changes. Please try again.", { module: 'save', ...opts });
}

export function reportLoadError(scope: string, err: unknown, opts?: Parameters<typeof reportError>[3]) {
  reportError(scope, err, "We couldn't load this data. Please refresh.", { module: 'load', ...opts });
}

/**
 * Produce a user-safe message for an unknown error. We prefer surfacing the
 * real backend detail — a truthful "Row-level security denied insert on
 * campaign_content" beats a mystery "Something went wrong" every time. Only
 * genuinely internal-looking noise (raw SQL syntax, JSON parse errors) falls
 * back to the generic message. Keep the deny-list narrow.
 */
export function friendlyMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  const detail = readableDetail(err);
  if (!detail) return fallback;
  const lower = detail.toLowerCase();
  const alwaysHide = [
    'syntax error at or near',
    'unexpected token',
    'invalid input syntax for',
    'operator does not exist',
    'jwsverify',
    'jws signature',
  ];
  if (alwaysHide.some((k) => lower.includes(k))) return fallback;
  // Translate common technical patterns into human wording, but keep the
  // real detail so the user can act on it.
  let m = detail
    .replace(/new row violates row-level security policy( for table "?(.+?)"?)?/i,
      (_, __, tbl) => `You don't have permission to save this${tbl ? ` (${tbl})` : ''}. Contact your agency if you believe this is wrong.`)
    .replace(/permission denied for (table|relation|schema) (.+)/i,
      (_, __, tbl) => `Permission denied on ${tbl}. Contact your agency.`)
    .replace(/^payload too large$/i, 'File is too large for this workspace. Please compress and try again.')
    .replace(/failed to fetch/i, 'Network error — check your connection and retry.');
  if (m.length > 260) m = m.slice(0, 260) + '…';
  return m;
}

/**
 * Lightweight toast helper for mutation onError handlers. Surfaces the real
 * backend detail (headline + description) and records a system_events entry.
 */
export function toastError(err: unknown, fallback?: string, scope?: string) {
  const detail = friendlyMessage(err, fallback ?? 'Something went wrong. Please try again.');
  const headline = fallback ?? 'Action failed';
  // eslint-disable-next-line no-console
  console.error(`[${scope ?? 'toastError'}]`, err);
  if (detail && detail !== headline) {
    toast.error(headline, { description: detail });
  } else {
    toast.error(headline);
  }
  const { message, stack, code } = extractErr(err);
  void logSystemEvent({
    severity: 'error',
    module: 'save',
    message: `[${scope ?? 'toastError'}] ${message}`,
    stack: stack ?? null,
    context: { code, headline },
  });
}

/**
 * Diagnostic log — for tracing a multi-stage workflow (validation → storage →
 * db → notify). Writes an info-level system_events row so we can trace a
 * creator submission end-to-end even when nothing threw.
 */
export function logDiagnostic(scope: string, stage: string, data: Record<string, unknown> = {}) {
  // eslint-disable-next-line no-console
  console.info(`[${scope}] ${stage}`, data);
  void logSystemEvent({
    severity: 'info',
    module: 'save',
    message: `[${scope}] ${stage}`,
    context: { stage, ...data },
  });
}

